import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';
import classNames from 'classnames';
import { gsap, Power1 } from 'gsap';
import Lottie from 'lottie-react';

import { SCREEN_SIZES } from '@/shared/Constants';
import { supportsTouch } from '@/playlist/games/laneChooser/Helpers';

import Button from '@/components/button/Button';
import Tooltip, { TooltipColors, TooltipSizes } from '@/components/tooltip/Tooltip';

import leftKeyInstruction from '@/playlist/games/laneChooser/assets/lottie/left-key-instruction.json';
import spaceKeyInstruction from '@/playlist/games/laneChooser/assets/lottie/space-key-instruction.json';
import swipeLeftInstruction from '@/playlist/games/laneChooser/assets/lottie/swipe-left-instruction.json';
import swipeUpInstruction from '@/playlist/games/laneChooser/assets/lottie/swipe-up-instruction.json';
import tapInstruction from '@/playlist/games/laneChooser/assets/lottie/tap-instruction.json';
import upKeyInstruction from '@/playlist/games/laneChooser/assets/lottie/up-key-instruction.json';

type Stage = 'leftRight' | 'up' | 'down' | 'space' | 'done';

const TOOLTIP_ANCHOR_ID = 'lane-chooser-hint-tooltip-anchor';

export type HintProps = {
	className?: string;
	enableKeys?: boolean; // when true, key presses advance the staged hints
	onComplete?: () => void; // called when instructions are finished
	initialStage?: Stage; // optional: start from a specific stage (e.g., 'space')
	showSkip?: boolean; // optional: show skip button (default true)
};

export type KeyInstructionsHandle = {
	advanceOnSwipeLeft: () => void;
	advanceOnSwipeRight: () => void;
	advanceOnSwipeUp: () => void;
	advanceOnTap: () => void;
};

const KeyInstructions = forwardRef<KeyInstructionsHandle, HintProps>(
	(
		{ className, enableKeys = false, onComplete, initialStage = 'leftRight', showSkip = true },
		ref,
	) => {
		const { t } = useTranslation();
		const [useSwipeInstructions, setUseSwipeInstructions] = useState(false);
		const [stage, setStage] = useState<Stage>(initialStage);

		// Expose methods to parent via ref
		useImperativeHandle(
			ref,
			() => ({
				advanceOnSwipeLeft: () => {
					if (stage === 'leftRight') setStage('up');
				},
				advanceOnSwipeRight: () => {
					if (stage === 'leftRight') setStage('up');
				},
				advanceOnSwipeUp: () => {
					if (stage === 'up') setStage('space');
				},
				advanceOnTap: () => {
					if (stage === 'space') setStage('done');
				},
			}),
			[stage],
		);

		useEffect(() => {
			if (typeof window === 'undefined') return;
			const updateSwipeMode = () => {
				const prefersSwipe = window.innerWidth < SCREEN_SIZES.TABLET || supportsTouch();
				setUseSwipeInstructions(prefersSwipe);
			};
			updateSwipeMode();
			window.addEventListener('resize', updateSwipeMode);
			return () => window.removeEventListener('resize', updateSwipeMode);
		}, []);

		useEffect(() => {
			if (!enableKeys || stage === 'done') return;
			const onKeyDown = (e: KeyboardEvent) => {
				if (stage === 'leftRight' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
					setStage('up');
				} else if (
					(stage === 'up' || stage === 'down') &&
					(e.key === 'ArrowUp' || e.key === 'ArrowDown')
				) {
					setStage('space');
				} else if (stage === 'space' && (e.code === 'Space' || e.key === ' ')) {
					setStage('done');
				}
			};
			window.addEventListener('keydown', onKeyDown);
			return () => window.removeEventListener('keydown', onKeyDown);
		}, [enableKeys, stage]);

		const swipeHandlers = useSwipeable({
			onSwipedLeft: () => {
				if (!enableKeys || !useSwipeInstructions || stage !== 'leftRight') return;
				setStage('up');
			},
			onSwipedUp: () => {
				if (!enableKeys || !useSwipeInstructions || stage !== 'up') return;
				setStage('space');
			},
			onTap: () => {
				if (!enableKeys || !useSwipeInstructions || stage !== 'space') return;
				setStage('done');
			},
			trackTouch: true,
			trackMouse: false,
		});

		useEffect(() => {
			if (!enableKeys || !useSwipeInstructions || stage === 'done') return;
			if (typeof document === 'undefined') return;
			const target = document.body;
			if (!target) return;
			swipeHandlers.ref(target);
			return () => swipeHandlers.ref(null);
		}, [enableKeys, stage, swipeHandlers, useSwipeInstructions]);

		// GSAP fade container ref and derived display stage
		const containerRef = useRef<HTMLDivElement | null>(null);
		const [displayStage, setDisplayStage] = useState<
			'leftRight' | 'up' | 'down' | 'space' | 'done'
		>(stage);

		// When stage changes, perform GSAP fade transition
		useEffect(() => {
			if (stage === displayStage) return;
			const el = containerRef.current;

			// fade out current
			if (stage === 'done') {
				gsap.to(el, {
					autoAlpha: 0,
					duration: 0.2,
					ease: Power1.easeOut,
					onComplete: () => {
						setDisplayStage('done');
						onComplete && onComplete();
					},
				});
				return;
			}

			const tl = gsap.timeline();
			tl.to(el, { autoAlpha: 0, duration: 0.2, ease: Power1.easeOut })
				.add(() => {
					setDisplayStage(stage);
				})
				.to(el, { autoAlpha: 1, duration: 0.2, ease: Power1.easeOut });
		}, [stage, displayStage]);

		// Ensure initial state is visible
		useEffect(() => {
			if (containerRef.current) gsap.set(containerRef.current, { autoAlpha: 1 });
		}, []);

		if (displayStage === 'done') return null;

		const key = (() => {
			if (useSwipeInstructions) {
				if (displayStage === 'leftRight') return 'lane_chooser.hints.swipe_left';
				if (displayStage === 'up') return 'lane_chooser.hints.swipe_up';
				return 'lane_chooser.hints.tap';
			}
			if (displayStage === 'leftRight') return 'lane_chooser.hints.press_left_right';
			if (displayStage === 'up') return 'lane_chooser.hints.press_up_or_down';
			return 'lane_chooser.hints.press_space';
		})();
		const text = t(key);
		const animationData = (() => {
			if (useSwipeInstructions) {
				if (displayStage === 'leftRight') return swipeLeftInstruction;
				if (displayStage === 'up') return swipeUpInstruction;
				return tapInstruction;
			}
			if (displayStage === 'leftRight') return leftKeyInstruction;
			if (displayStage === 'up') return upKeyInstruction;
			return spaceKeyInstruction;
		})();
		const lottieClassName = classNames('lane-chooser__hint-keys', {
			'lane-chooser__hint-lottie--swipe-down':
				useSwipeInstructions && (displayStage === 'up' || displayStage === 'down'),
		});

		return (
			<div
				className="lane-chooser__hint"
				ref={containerRef}
				style={{ pointerEvents: showSkip ? 'auto' : 'none' }}
			>
				<div
					id={TOOLTIP_ANCHOR_ID}
					className={classNames('lane-chooser__hint-lottie', className)}
					role="note"
					aria-label={`Hint: ${text}`}
				>
					<Lottie
						className={lottieClassName}
						animationData={animationData}
						loop
						autoplay
					/>
				</div>
				<Tooltip
					place="top"
					title={text}
					color={TooltipColors.LIGHT}
					size={TooltipSizes.LARGE}
					className="lane-chooser__hint-tooltip"
					anchor={TOOLTIP_ANCHOR_ID}
					isOpen
				/>
				{showSkip && (
					<div className="lane-chooser__hint-skip">
						<Button
							type="primary"
							iconName="icon-check"
							title={t('lane_chooser.hints.skip')}
							onClick={() => setStage('done')}
						/>
					</div>
				)}
			</div>
		);
	},
);

export default KeyInstructions;
